import React, { createContext, useState, useContext, useEffect } from "react";
import { login, signup, logout, getCurrentUser } from "../services/authService"; // We'll create these mocks

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Check initial auth status

  useEffect(() => {
    // Try to load user from storage/session on initial load
    const loadUser = async () => {
      setLoading(true);
      try {
        const currentUser = await getCurrentUser(); // Mock async check
        setUser(currentUser);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  const loginUser = async (credentials) => {
    setLoading(true);
    try {
      const loggedInUser = await login(credentials);
      setUser(loggedInUser);
      setLoading(false);
      return loggedInUser;
    } catch (error) {
      setUser(null);
      setLoading(false);
      throw error; // Re-throw to handle in login form
    }
  };

  const signupUser = async (details) => {
    // In a real app, decide if signup automatically logs in
    setLoading(true);
    try {
      const newUser = await signup(details);
      // Maybe log them in directly: setUser(newUser);
      setLoading(false);
      return newUser; // Or maybe just a success message
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logoutUser = async () => {
    setLoading(true);
    try {
      await logout();
      setUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
      // Decide how to handle failed logout
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === "administrator",
    isManager: user?.role === "manager",
    role: user?.role,
    loading,
    loginUser,
    signupUser,
    logoutUser,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
